import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import { Badge, Button, FormattedDateTime, Pagination } from '../components'
import { useArticleFilters } from '../hooks/useArticleFilters'
import { useDebounce } from '../hooks/useDebounce'
import { useWidgetSite } from '../contexts/WidgetSiteContext'
import { useToast } from '../contexts/ToastContext'
import { createArticle, fetchArticles, type Article, type ArticleStage } from '../services/articlesApi'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import styles from './ArticlesList.module.scss'

const STAGE_VARIANT: Record<ArticleStage, 'success' | 'warning'> = {
  published: 'success',
  draft: 'warning',
}

const ArticlesList: React.FC = () => {
  const { siteId } = useWidgetSite()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { filters, updateFilterAndResetPage, handlePageChange } = useArticleFilters()
  const { search, startDate, endDate, page } = filters
  const limit = 50

  const [searchInput, setSearchInput] = useState(search)
  const debouncedSearch = useDebounce(searchInput, 300)
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    if (debouncedSearch !== search) {
      updateFilterAndResetPage({ search: debouncedSearch })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  useEffect(() => {
    setSearchInput(search)
  }, [search])

  const { data, isLoading, error, mutate } = useSWR(
    ['articles', { siteId, search, startDate, endDate, page, limit }],
    () =>
      fetchArticles({
        site_id: siteId,
        search: search || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page,
        limit,
      }),
    { revalidateOnFocus: false },
  )

  const handleCreateArticle = async () => {
    setIsCreating(true)
    try {
      const created = await createArticle({ site_id: siteId })
      navigate(`/content/articles/${created.tc_article_id}`)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to create article', 'error')
    } finally {
      setIsCreating(false)
    }
  }

  const articles: Article[] = data?.articles || []
  const pagination = data?.pagination

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading articles</h2>
          <p>Failed to fetch articles: {(error as Error).message}</p>
          <Button variant="secondary" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.pageHeader}>
        <h1>Articles</h1>
        <div className={pageStyles.headerButtons}>
          <Button onClick={handleCreateArticle} disabled={isCreating}>
            {isCreating ? 'Creating…' : '+ New Article'}
          </Button>
        </div>
      </div>

      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={styles.dateFilters}>
            <div className={styles.dateField}>
              <label htmlFor="startDate">Published After:</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => updateFilterAndResetPage({ startDate: e.target.value })}
                className={listTableStyles.searchInput}
              />
            </div>
            <div className={styles.dateField}>
              <label htmlFor="endDate">Published Before:</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => updateFilterAndResetPage({ endDate: e.target.value })}
                className={listTableStyles.searchInput}
              />
            </div>
            <div className={styles.dateField}>
              <label htmlFor="search">Search:</label>
              <input
                type="text"
                id="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Title, headline, or byline"
                className={listTableStyles.searchInput}
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading articles...</h3>
        </div>
      ) : articles.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No articles found</h3>
          <p>No articles match the selected filters.</p>
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Title</div>
            <div>Byline</div>
            <div>Published</div>
            <div>Stage</div>
          </div>

          <div className={listTableStyles.tableBody}>
            {articles.map((article) => (
              <div
                key={article.tc_article_id}
                className={`${listTableStyles.tableRow} ${styles.listItem}`}
              >
                <div className={listTableStyles.cellPrimary}>
                  <Link
                    to={`/content/articles/${article.tc_article_id}`}
                    className={styles.titleLink}
                  >
                    <div className={styles.titleCell}>
                      <p className={listTableStyles.primaryText}>
                        {article.title || '(untitled)'}
                      </p>
                      {article.headline && (
                        <p className={styles.headline}>{article.headline}</p>
                      )}
                    </div>
                  </Link>
                </div>

                <div className={listTableStyles.cellText}>
                  {article.byline || '—'}
                </div>

                <div className={listTableStyles.cellText}>
                  {article.published_at ? (
                    <FormattedDateTime dateString={article.published_at} />
                  ) : (
                    '—'
                  )}
                </div>

                <div className={listTableStyles.cellText}>
                  <Badge variant={STAGE_VARIANT[article.stage] || 'neutral'}>
                    {article.stage}
                  </Badge>
                </div>
              </div>
            ))}
          </div>

          {pagination && pagination.total_pages > 1 && (
            <Pagination pagination={pagination} onPageChange={handlePageChange} />
          )}
        </>
      )}
    </div>
  )
}

export default ArticlesList
